const PRODUCT_BRAND_CONFIG_PATH = '/_runtime/product-brand-gateway'
const RUNTIME_CONFIG_TIMEOUT_MS = 3_000

const BUILD_TIME_CONFIGURATION_HOSTS = new Set([
  '127.0.0.1',
  'admin.apostra.com',
  'admin.interchange.io',
  'admin.staging.apostra.com',
  'admin.staging.interchange.io',
  'localhost',
])

function canUseBuildTimeConfiguration(hostname: string | undefined): boolean {
  return (
    !hostname ||
    BUILD_TIME_CONFIGURATION_HOSTS.has(hostname) ||
    hostname.endsWith('.scope3.workers.dev') ||
    hostname.endsWith('.trycloudflare.com')
  )
}

function isHttpsOrigin(value: unknown): boolean {
  if (typeof value !== 'string') return false
  try {
    const url = new URL(value)
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      url.pathname === '/' &&
      !url.search &&
      !url.hash
    )
  } catch {
    return false
  }
}

function isHttpsUrl(value: unknown): boolean {
  if (typeof value !== 'string') return false
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && !url.username && !url.password
  } catch {
    return false
  }
}

function isGatewayForHostname(value: unknown, hostname: string): boolean {
  if (typeof value !== 'object' || value === null) return false
  const entry = value as Record<string, unknown>
  if (
    !isHttpsOrigin(entry.appOrigin) ||
    !isHttpsOrigin(entry.apiOrigin) ||
    !isHttpsOrigin(entry.adminUiUrl) ||
    !isHttpsUrl(entry.wordmarkUrl)
  ) {
    return false
  }
  return new URL(entry.adminUiUrl as string).hostname === hostname
}

export async function loadProductBrandGatewayConfig(
  fetcher: typeof fetch = fetch,
  timeoutMs = RUNTIME_CONFIG_TIMEOUT_MS,
  hostname = globalThis.location?.hostname,
): Promise<void> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetcher(PRODUCT_BRAND_CONFIG_PATH, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
    if (!response.ok) {
      throw new Error(
        `Runtime gateway configuration returned ${response.status}`,
      )
    }
    const value: unknown = await response.json()
    if (!Array.isArray(value)) {
      throw new Error('Runtime gateway configuration must be an array')
    }
    if (
      !canUseBuildTimeConfiguration(hostname) &&
      (value.length !== 1 || !isGatewayForHostname(value[0], hostname))
    ) {
      throw new Error(
        'Dedicated gateways require one valid exact-host runtime configuration',
      )
    }
    ;(
      globalThis as typeof globalThis & {
        __PRODUCT_BRAND_GATEWAYS__?: unknown
      }
    ).__PRODUCT_BRAND_GATEWAYS__ = value
  } catch (error) {
    if (!canUseBuildTimeConfiguration(hostname)) {
      throw new Error('Unable to load dedicated gateway configuration', {
        cause: error,
      })
    }
    // Known legacy and local hosts retain their build-time configuration.
  } finally {
    clearTimeout(timeout)
  }
}
